import { useMemo } from 'react';
import {
  Route,
  GitBranch,
  Crosshair,
  Flame,
  ShieldCheck,
  Activity,
} from 'lucide-react';

interface Finding {
  id?: string;
  title: string;
  severity: string;
  scan_type?: string;
  cvss_v4?: { score: number };
  cvss_v4_score?: number;
  compliance?: Record<string, string[]>;
}

interface AttackChainStep {
  step: number;
  module: string;
  finding: string;
}

interface AttackChain {
  title: string;
  severity: string;
  business_impact?: string;
  steps: AttackChainStep[];
}

interface GraphMetricsProps {
  findings: Finding[];
  attackChains: AttackChain[];
}

const SEVERITY_WEIGHT: Record<string, number> = {
  critical: 10,
  high: 7,
  medium: 4,
  low: 2,
  info: 0.5,
};

const MITRE_PHASES = [
  'reconnaissance',
  'resource-development',
  'initial-access',
  'execution',
  'persistence',
  'privilege-escalation',
  'defense-evasion',
  'credential-access',
  'discovery',
  'lateral-movement',
  'collection',
  'command-and-control',
  'exfiltration',
  'impact',
];

function getSeverityWeight(severity: string): number {
  return SEVERITY_WEIGHT[severity?.toLowerCase()] ?? 1;
}

function getCvssScore(finding: Finding): number {
  if (finding.cvss_v4?.score != null) return finding.cvss_v4.score;
  if (finding.cvss_v4_score != null) return finding.cvss_v4_score;
  return getSeverityWeight(finding.severity);
}

function computeMetrics(
  findings: Finding[],
  chains: AttackChain[],
): {
  totalPaths: number;
  shortestPath: number;
  mostConnectedNode: string;
  mostConnectedCount: number;
  blastRadius: number;
  defenseCoverage: number;
  riskScore: number;
} {
  const totalPaths = chains.length || 0;

  const shortestPath =
    chains.length > 0
      ? Math.min(...chains.map((c) => c.steps?.length ?? 0))
      : 0;

  const nodeAppearances = new Map<string, number>();
  for (const chain of chains) {
    for (const step of chain.steps ?? []) {
      const key = step.finding || step.module;
      nodeAppearances.set(key, (nodeAppearances.get(key) ?? 0) + 1);
    }
  }

  let mostConnectedNode = 'N/A';
  let mostConnectedCount = 0;
  for (const [node, count] of nodeAppearances) {
    if (count > mostConnectedCount) {
      mostConnectedCount = count;
      mostConnectedNode = node;
    }
  }

  const criticalFindings = findings.filter(
    (f) => f.severity === 'critical' || f.severity === 'high',
  );
  const criticalModules = new Set(criticalFindings.map((f) => f.scan_type));
  let reachableCount = 0;
  for (const chain of chains) {
    const touchesCritical = chain.steps?.some(
      (s) => criticalModules.has(s.module),
    );
    if (touchesCritical) {
      reachableCount += chain.steps?.length ?? 0;
    }
  }
  const blastRadius = Math.min(reachableCount, findings.length);

  const detectedPhases = new Set<string>();
  for (const f of findings) {
    const comp = f.compliance ?? {};
    for (const framework of Object.keys(comp)) {
      const controls = comp[framework] ?? [];
      for (const ctrl of controls) {
        const lower = ctrl.toLowerCase();
        for (const phase of MITRE_PHASES) {
          if (lower.includes(phase) || lower.includes('mitre')) {
            detectedPhases.add(phase);
          }
        }
      }
    }
    const scanLower = (f.scan_type ?? '').toLowerCase();
    if (scanLower.includes('recon') || scanLower === 'dns' || scanLower === 'whois') {
      detectedPhases.add('reconnaissance');
    }
    if (scanLower === 'headers' || scanLower === 'ssl' || scanLower === 'tls') {
      detectedPhases.add('defense-evasion');
    }
    if (scanLower === 'web' || scanLower === 'sqli' || scanLower === 'xss') {
      detectedPhases.add('initial-access');
    }
    if (scanLower === 'credentials' || scanLower === 'brute') {
      detectedPhases.add('credential-access');
    }
  }
  const defenseCoverage =
    MITRE_PHASES.length > 0
      ? Math.round((detectedPhases.size / MITRE_PHASES.length) * 100)
      : 0;

  let riskScore = 0;
  for (const f of findings) {
    const base = getCvssScore(f);
    const connectivity = nodeAppearances.size > 0
      ? (nodeAppearances.get(f.title) ?? 1)
      : 1;
    riskScore += base * connectivity;
  }
  riskScore = Math.min(Math.round(riskScore * 10) / 10, 100);

  return {
    totalPaths,
    shortestPath,
    mostConnectedNode,
    mostConnectedCount,
    blastRadius,
    defenseCoverage,
    riskScore,
  };
}

interface MetricCardProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string | number;
  explanation: string;
  color: string;
  accentBg: string;
}

function MetricCard({ icon: Icon, label, value, explanation, color, accentBg }: MetricCardProps): JSX.Element {
  return (
    <div
      style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 12,
        padding: '20px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minHeight: 140,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            background: accentBg,
            borderRadius: 8,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon style={{ width: 18, height: 18, color }} />
        </div>
        <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>
          {label}
        </span>
      </div>
      <div style={{ color, fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ color: '#64748b', fontSize: 11, lineHeight: 1.4 }}>
        {explanation}
      </div>
    </div>
  );
}

export default function GraphMetrics({ findings, attackChains }: GraphMetricsProps): JSX.Element {
  const metrics = useMemo(
    () => computeMetrics(findings ?? [], attackChains ?? []),
    [findings, attackChains],
  );

  const riskColor = metrics.riskScore > 60 ? '#ef4444' : metrics.riskScore > 30 ? '#f59e0b' : '#22c55e';
  const coverageColor = metrics.defenseCoverage >= 60 ? '#22c55e' : metrics.defenseCoverage >= 30 ? '#f59e0b' : '#ef4444';

  return (
    <div
      style={{
        background: '#0f172a',
        borderRadius: 12,
        padding: 20,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 14,
        }}
      >
        <MetricCard
          icon={Route}
          label="Total Attack Paths"
          value={metrics.totalPaths}
          explanation="Unique chains from entry point to business impact identified during analysis."
          color="#f59e0b"
          accentBg="rgba(245,158,11,0.12)"
        />
        <MetricCard
          icon={Crosshair}
          label="Shortest Path"
          value={metrics.shortestPath > 0 ? `${metrics.shortestPath} steps` : 'N/A'}
          explanation="Minimum steps an attacker needs from target to critical impact."
          color={metrics.shortestPath <= 2 ? '#ef4444' : metrics.shortestPath <= 4 ? '#f59e0b' : '#22c55e'}
          accentBg={metrics.shortestPath <= 2 ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)'}
        />
        <MetricCard
          icon={GitBranch}
          label="Most Connected Node"
          value={metrics.mostConnectedCount > 0 ? `${metrics.mostConnectedCount} chains` : 'N/A'}
          explanation={
            metrics.mostConnectedNode !== 'N/A'
              ? `"${metrics.mostConnectedNode}" appears in the most attack chains.`
              : 'No attack chain data available.'
          }
          color="#a78bfa"
          accentBg="rgba(167,139,250,0.12)"
        />
        <MetricCard
          icon={Flame}
          label="Blast Radius"
          value={`${metrics.blastRadius} / ${findings?.length ?? 0}`}
          explanation="Findings reachable if the most critical vulnerability is exploited."
          color={metrics.blastRadius > (findings?.length ?? 0) * 0.5 ? '#ef4444' : '#f59e0b'}
          accentBg="rgba(239,68,68,0.12)"
        />
        <MetricCard
          icon={ShieldCheck}
          label="Defense Coverage"
          value={`${metrics.defenseCoverage}%`}
          explanation={`${metrics.defenseCoverage}% of MITRE ATT&CK phases have at least one detection.`}
          color={coverageColor}
          accentBg={metrics.defenseCoverage >= 60 ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)'}
        />
        <MetricCard
          icon={Activity}
          label="Risk Score"
          value={metrics.riskScore}
          explanation="Weighted sum of severity and connectivity across all findings (0-100)."
          color={riskColor}
          accentBg={metrics.riskScore > 60 ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)'}
        />
      </div>
    </div>
  );
}
